import { track } from "@/lib/analytics";

/**
 * Studio (video editör) olayları — tek noktadan Mixpanel + GTM dataLayer'a yazar.
 *
 * `/studio` bugüne kadar hiç ölçülmüyordu: ne sidebar girişinde, ne medya
 * ekleme adımında, ne de export'ta tek bir olay vardı. Funnel'ın kırılma
 * noktaları burada toplanıyor:
 *
 *   studio_clicked        → kullanıcı studio'ya girmek istedi
 *   new_project_clicked   → boş bir projeye başladı
 *   library_add_clicked   → projeye medya eklemeye başladı
 *   export_started        → video hazır, render kuyruğa girdi
 *
 * Netmera bilinçli olarak kapsam dışı: Netmera olayları panelde tanımlanan
 * 5 karakterlik kodlara bağlı (bkz. `src/window.d.ts`) ve bu olaylar için
 * henüz kod atanmadı. Kod gelince buraya `window.netmera.push(...)` eklemek
 * yeterli — çağrı noktalarının değişmesi gerekmez.
 *
 * `track()` zaten hata yutuyor; buradaki hiçbir çağrı kullanıcı akışını bozmaz.
 */

/** Medya, kütüphane seçicisinden mi yoksa yerel dosyadan mı ekleniyor. */
export type LibraryAddSource = "library" | "upload";

/**
 * Kütüphane seçicisinde tıklanan filtre; "all" genel "Browse Library"
 * butonu, "mixed" ise tek seferde farklı türde dosya yüklendiği durum.
 */
export type LibraryAddMediaType =
  | "image"
  | "video"
  | "audio"
  | "all"
  | "mixed";

/**
 * Boş string'i null'a çevirir.
 *
 * SDK export payload'ını `bundle.metadata.id ?? ""` ile kuruyor ve
 * `bundle.metadata.id`, SDK'nın kendi `project.projectId` alanı — `null`
 * başlıyor ve backend `_id`'si oraya hiç yazılmıyor. Yani henüz kaydedilmemiş
 * bir projede `payload.projectId` boş string geliyor; `??` bunu yakalamadığı
 * için olay `project_id: ""` ile düşüyordu. Analytics tarafında "boş değer" ile
 * "değer yok" ayrımı kalmasın diye tüm kimlik/ad alanları buradan geçiyor.
 */
const blankToNull = (value?: string | null): string | null => {
  const trimmed = typeof value === "string" ? value.trim() : value;
  return trimmed ? trimmed : null;
};

/** Sidebar'daki Studio navigasyon öğesine tıklandı. */
export const trackStudioClicked = (): void => {
  track("studio_clicked", { source: "sidebar" });
};

/**
 * "My Media" panelinde medya eklemeye başlandı.
 *
 * İki giriş noktası var ve `source` ile ayrışıyorlar: kütüphane seçicisini
 * açan "Browse Library" butonları (`library`) ve sürükle-bırak/yerel dosya
 * yükleme (`upload`).
 */
export const trackLibraryAddClicked = (props: {
  source: LibraryAddSource;
  mediaType: LibraryAddMediaType;
  /** Yerel yüklemede kullanıcının seçtiği dosya sayısı. */
  fileCount?: number;
}): void => {
  track("library_add_clicked", {
    source: props.source,
    media_type: props.mediaType,
    ...(props.fileCount !== undefined && { file_count: props.fileCount }),
  });
};

/**
 * Kullanıcı export'u başlattı — render backend'e gönderilmeden hemen önce.
 *
 * Başarı değil, niyet ölçülüyor: render Lambda'da dakikalarca sürebiliyor ve
 * kullanıcı sekmeyi kapatabiliyor. Tamamlanma ayrı bir olay olarak eklenmeli.
 */
export const trackExportStarted = (props: {
  /** Backend `_id`'si; proje hiç kaydedilmediyse boş/`null` gelir. */
  projectId?: string | null;
  projectName?: string | null;
  profileId?: string | null;
  profileName?: string | null;
  width?: number | null;
  height?: number | null;
  fps?: number | null;
  durationFrames?: number | null;
  itemCount?: number | null;
  trackCount?: number | null;
}): void => {
  const { fps, durationFrames } = props;
  // Saniye cinsinden süre, GA4 tarafında frame'den daha okunur.
  const durationSeconds =
    fps && durationFrames ? Math.round(durationFrames / fps) : null;

  track("export_started", {
    project_id: blankToNull(props.projectId),
    // Proje henüz kaydedilmediyse backend kaydı yok; olay yine de düşmeli.
    project_saved: Boolean(blankToNull(props.projectId)),
    project_name: blankToNull(props.projectName),
    profile_id: blankToNull(props.profileId),
    profile_name: blankToNull(props.profileName),
    width: props.width ?? null,
    height: props.height ?? null,
    fps: fps ?? null,
    duration_frames: durationFrames ?? null,
    duration_seconds: durationSeconds,
    item_count: props.itemCount ?? null,
    track_count: props.trackCount ?? null,
  });
};

/**
 * "New Project" butonunun bulunduğu giriş noktası.
 *
 * Üçü de `/studio/projects` sayfasında ve aynı akışı (boş editörü açmak)
 * tetikliyor; hangisinin kullanıldığını ayırmak, grid'deki kartın mı yoksa
 * boş durum ekranının mı gerçekten iş gördüğünü göstermek için.
 */
export type NewProjectSource = "card" | "empty_state" | "quick_menu";

/** Kullanıcı yeni proje açmak için tıkladı — editöre yönlendirmeden hemen önce. */
export const trackNewProjectClicked = (props: {
  source: NewProjectSource;
}): void => {
  track("new_project_clicked", { source: props.source });
};
